import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { Button } from '../Button2';
import styles from './SettingsModal.module.css';

/**
 * Shared settings-dialog shell — ChatGPT-style side-nav + right
 * content pane. Chrome is intentionally visually aligned with
 * `FormModal` so the two modal types feel part of one design system
 * (same overlay alpha, fade-in animation, slide-in container).
 *
 * Ported verbatim from Themis (`cybpass-themis-agent/frontend/src/
 * components/ui/SettingsModal/SettingsModal.jsx`). JSX logic is
 * byte-for-byte identical; only the CSS layer was retargeted onto
 * Athena's design tokens.
 *
 * Usage:
 *   <SettingsModal
 *     isOpen
 *     onClose={...}
 *     title="Settings"
 *     sections={[
 *       { id: 'overview', label: 'Overview', icon: <Icon />, group: 'Overview'   },
 *       { id: 'users',    label: 'Users',    icon: <Icon />, group: 'Management' },
 *       { id: 'roles',    label: 'Roles',    icon: <Icon />, group: 'Management' },
 *     ]}
 *     activeSectionId={id}               // optional — controlled mode
 *     onSectionChange={setId}            // optional — controlled mode
 *   >
 *     {id === 'overview' && <OverviewPanel />}
 *     ...
 *   </SettingsModal>
 *
 * If `activeSectionId`/`onSectionChange` are omitted, the component
 * falls back to internal state keyed off the first section.
 */
const SettingsModal = ({
  isOpen,
  onClose,
  title,
  sections = [],
  activeSectionId,
  onSectionChange,
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
  ariaLabel,
}) => {
  const closeButtonRef = useRef(null);
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Uncontrolled fallback — first section is active by default.
  const [internalId, setInternalId] = useState(sections[0]?.id);
  const isControlled = activeSectionId !== undefined && onSectionChange !== undefined;
  const currentId = isControlled ? activeSectionId : internalId;
  const setCurrentId = isControlled ? onSectionChange : setInternalId;

  // Focus management: save whatever was focused before we open so we
  // can restore it when the modal closes. On open, focus the close
  // button so keyboard users have a predictable anchor.
  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const t = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeOnEscape, onClose]);

  // Group sections by their `group` field while preserving order.
  const grouped = useMemo(() => {
    const order = [];
    const byGroup = new Map();
    sections.forEach((s) => {
      const key = s.group ?? '';
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key).push(s);
    });
    return order.map((key) => ({ group: key, items: byGroup.get(key) }));
  }, [sections]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  return createPortal(
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div
        ref={containerRef}
        className={styles.container}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Settings'}
      >
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Button
              ref={closeButtonRef}
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label="Close"
            >
              <Icon name="close" size={18} />
            </Button>
            {title && <h2 className={styles.title}>{title}</h2>}
          </div>

          <nav className={styles.nav} aria-label="Settings sections">
            {grouped.map(({ group, items }) => (
              <div key={group || '__ungrouped'} className={styles.navGroup}>
                {group && <h3 className={styles.navGroupTitle}>{group}</h3>}
                {items.map((section) => {
                  const isActive = section.id === currentId;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                      onClick={() => setCurrentId(section.id)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {section.icon && <span className={styles.navIcon}>{section.icon}</span>}
                      <span className={styles.navLabel}>{section.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        <div className={styles.content}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SettingsModal;
